import { useEffect } from "react";
import { PLANS } from "../../lib/pricing";

/**
 * One schema.org Offer for a plan in a given currency.
 *
 * Razorpay is the only gateway, so INR is what is actually charged and USD is
 * the conversion — the same relationship the on-page price expresses as
 * "₹249/mo (≈$5/mo)". Both are published so an engine asked "how much is
 * Tadlo in rupees?" has an answer.
 */
function offerFor(plan, currency) {
  return {
    "@type": "Offer",
    name: `${plan.name} plan`,
    price: String(currency === "INR" ? plan.inr : plan.usd),
    priceCurrency: currency,
    availability: "https://schema.org/InStock",
    ...(plan.inr === 0
      ? { description: "Free forever — unlimited DMs and unlimited automations, no credit card" }
      : {
          // "per month" belongs in a UnitPriceSpecification, NOT as
          // billingIncrement/unitText on the Offer itself — those are not
          // properties of Offer and would not validate. unitCode "MON" is
          // the UN/CEFACT common code for a month.
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: String(currency === "INR" ? plan.inr : plan.usd),
            priceCurrency: currency,
            unitCode: "MON",
            billingDuration: 1,
          },
        }),
  };
}

/**
 * PageSEO — imperative <head/> manager for SPA marketing pages.
 *
 * Injects (and cleans up):
 *   - <title>
 *   - <meta name="description">
 *   - <link rel="canonical">
 *   - Open Graph tags (og:title, og:description, og:image, og:url, og:type)
 *   - Twitter card tags
 *   - JSON-LD schema blocks (SoftwareApplication / Article / FAQPage)
 *
 * Why not react-helmet? We only need a few tags and the dep is abandoned.
 * Imperative DOM updates are fine for SPA routes and avoid the dep surface.
 *
 * Props:
 *   title, description, canonical, ogImage, ogType
 *   softwareAppSchema: bool — emit SoftwareApplication JSON-LD (feature pages)
 *   articleSchema: object  — emit Article JSON-LD (comparison pages)
 *   faqSchema: array       — emit FAQPage JSON-LD (any page with FAQs)
 *   itemListSchema: array  — emit ItemList JSON-LD (index/hub pages only);
 *                            [{name, url}] in the order the page shows them
 *   _schemaOnly: bool      — internal: skip <title>/<meta> when nested inside
 *                            another component (e.g. FAQSection)
 */

// The 1200x630 share card, generated by scripts/make-og-image.js. This was
// tadlo-logo.jpeg, which is 512x512 — while public/index.html declares
// og:image:width/height as 1200x630. So every page on the site advertised a
// landscape card and served a square logo, and platforms that trust the
// declared box rendered a broken or letterboxed preview.
const DEFAULT_OG_IMAGE = `${"https://tadlo.ai"}/tadlo-og.png`;
const DEFAULT_OG_DIMS = { width: "1200", height: "630" };
const SITE_URL = "https://tadlo.ai";

// Key under which we tag every node we own, so cleanup only removes ours.
const OWNER_ATTR = "data-tadlo-seo";

function upsertMeta({ name, property, content }) {
  if (content == null) return null;
  const selector = name
    ? `meta[name="${name}"]`
    : `meta[property="${property}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (property) el.setAttribute("property", property);
    el.setAttribute(OWNER_ATTR, "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function upsertLink({ rel, href }) {
  if (!href) return null;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(OWNER_ATTR, "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

function injectJsonLd(id, obj) {
  if (!obj) return null;
  // Remove any stale copy with same id first — React strict-mode re-runs
  // the effect on mount, and we don't want duplicate schema blocks.
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.id = id;
  s.setAttribute(OWNER_ATTR, "1");
  s.text = JSON.stringify(obj);
  document.head.appendChild(s);
  return s;
}

export default function PageSEO({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  softwareAppSchema = false,
  articleSchema = null,
  faqSchema = null,
  breadcrumbs = null,
  itemListSchema = null,
  _schemaOnly = false,
}) {
  useEffect(() => {
    // The fallback takes the PATH from the browser but never the ORIGIN:
    // prerendering runs against http://127.0.0.1:45678, so window.location
    // .origin would bake localhost into the canonical of any page that forgets
    // to pass one. Every marketing page does pass one today; this keeps the
    // next one that doesn't from shipping a broken URL.
    const canonicalUrl = canonical
      ? (canonical.startsWith("http") ? canonical : `${SITE_URL}${canonical}`)
      : typeof window !== "undefined"
      ? SITE_URL + window.location.pathname
      : SITE_URL;

    const created = [];

    if (!_schemaOnly) {
      if (title) {
        document.title = title;
      }
      if (description) {
        created.push(upsertMeta({ name: "description", content: description }));
      }
      created.push(upsertLink({ rel: "canonical", href: canonicalUrl }));

      // Open Graph
      created.push(upsertMeta({ property: "og:title", content: title }));
      created.push(
        upsertMeta({ property: "og:description", content: description })
      );
      created.push(upsertMeta({ property: "og:image", content: ogImage }));
      // Only assert dimensions we actually know. The shell hardcodes
      // 1200x630, which is true of the default card and a guess about any
      // other image — a blog post's own featured_image can be any size. An
      // absent dimension makes the platform measure the file itself, which
      // is always right; a wrong one is a broken preview.
      if (ogImage === DEFAULT_OG_IMAGE) {
        created.push(upsertMeta({ property: "og:image:width", content: DEFAULT_OG_DIMS.width }));
        created.push(upsertMeta({ property: "og:image:height", content: DEFAULT_OG_DIMS.height }));
      } else {
        for (const p of ["og:image:width", "og:image:height"]) {
          const stale = document.head.querySelector(`meta[property="${p}"]`);
          if (stale) stale.remove();
        }
      }
      created.push(upsertMeta({ property: "og:url", content: canonicalUrl }));
      created.push(upsertMeta({ property: "og:type", content: ogType }));
      created.push(
        upsertMeta({ property: "og:site_name", content: "Tadlo" })
      );

      // Twitter
      created.push(
        upsertMeta({ name: "twitter:card", content: "summary_large_image" })
      );
      created.push(upsertMeta({ name: "twitter:title", content: title }));
      created.push(
        upsertMeta({ name: "twitter:description", content: description })
      );
      created.push(upsertMeta({ name: "twitter:image", content: ogImage }));
    }

    // SoftwareApplication schema — feature pages
    if (softwareAppSchema) {
      injectJsonLd("ld-software-app", {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "tadlo.ai",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        // WAS: a single Offer at price "0", priceCurrency "USD" — which told
        // every engine that parsed this page that Tadlo is free and that the
        // paid tiers do not exist. Neither the ₹249 that customers actually
        // pay nor the ₹399 Pro plan appeared anywhere in the site's
        // structured data.
        //
        // AggregateOffer with the real range, and one Offer per plan per
        // currency, so INR is discoverable. Prices come from src/lib/pricing.js
        // — the same constants verify-prerender.js asserts the rendered HTML
        // against, so schema and page cannot disagree.
        offers: {
          "@type": "AggregateOffer",
          priceCurrency: "INR",
          lowPrice: String(PLANS.free.inr),
          highPrice: String(PLANS.pro.inr),
          offerCount: "3",
          offers: [
            offerFor(PLANS.free, "INR"),
            offerFor(PLANS.creator, "INR"),
            offerFor(PLANS.pro, "INR"),
            offerFor(PLANS.creator, "USD"),
            offerFor(PLANS.pro, "USD"),
          ],
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.8",
          reviewCount: "10000",
        },
      });
    }

    // Article schema — comparison pages
    if (articleSchema) {
      injectJsonLd("ld-article", {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: articleSchema.headline || title,
        description: articleSchema.description || description,
        image: articleSchema.image || ogImage,
        author: {
          "@type": "Organization",
          name: articleSchema.author || "Tadlo",
        },
        publisher: {
          "@type": "Organization",
          name: "Tadlo",
          logo: { "@type": "ImageObject", url: DEFAULT_OG_IMAGE },
        },
        datePublished: articleSchema.datePublished,
        dateModified:
          articleSchema.dateModified || articleSchema.datePublished,
        mainEntityOfPage: canonicalUrl,
      });
    }

    // BreadcrumbList — tells an engine where the page sits, and is what
    // renders the "tadlo.ai › Features › Follow gate" trail in a result
    // instead of a raw URL. Derived from the canonical path, so it cannot
    // disagree with the page's own address.
    if (breadcrumbs && breadcrumbs.length) {
      injectJsonLd("ld-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: b.url.startsWith("http") ? b.url : `${SITE_URL}${b.url}`,
        })),
      });
    }

    // ItemList — for INDEX pages (/features, /compare, /use-cases). Names the
    // things the page collects, in the order it presents them, so an engine can
    // read the hub as a set of routes rather than as one more article.
    //
    // Deliberately ItemList and not CollectionPage: CollectionPage describes the
    // page, ItemList describes its members, and the members are the reason an
    // index page exists. Each entry is a bare url — Google's guidance is that a
    // summary ItemList links out rather than restating each item inline.
    if (Array.isArray(itemListSchema) && itemListSchema.length > 0) {
      injectJsonLd("ld-itemlist", {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListOrder: "https://schema.org/ItemListOrderAscending",
        numberOfItems: itemListSchema.length,
        itemListElement: itemListSchema.map((it, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: it.name,
          url: it.url.startsWith("http") ? it.url : `${SITE_URL}${it.url}`,
        })),
      });
    }

    // FAQPage schema — any page with FAQs
    if (Array.isArray(faqSchema) && faqSchema.length > 0) {
      injectJsonLd("ld-faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqSchema.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: f.answer,
          },
        })),
      });
    }

    return () => {
      // Remove all meta/link nodes we created on unmount so the next
      // route starts clean. (document.title is reset by the next page.)
      created.forEach((node) => {
        if (node && node.getAttribute(OWNER_ATTR) === "1") node.remove();
      });
      ["ld-software-app", "ld-article", "ld-faq", "ld-breadcrumb", "ld-itemlist"].forEach((id) => {
        const n = document.getElementById(id);
        if (n && n.getAttribute(OWNER_ATTR) === "1") n.remove();
      });
    };
  }, [
    title,
    description,
    canonical,
    ogImage,
    ogType,
    softwareAppSchema,
    articleSchema,
    faqSchema,
    breadcrumbs,
    itemListSchema,
    _schemaOnly,
  ]);

  return null;
}
